import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'react-toastify'
import * as Blockly from 'blockly/core'
import type { WorkspaceSvg } from 'blockly/core'

import { useProject } from '../project/hook'
import { useTranslator } from '../translator/hook'
import {
  CONNECTION_STATUS_TIMEOUT_MS,
  getConnectionStatus,
} from '../project/api'
import { Breakpoint, useBreakpoint } from '../breakpoints'
import { missionManager } from '../../blocks/mission'
import { setTag } from '../../config/sentry'

// Studio presence is detected over HTTPS (school proxies block wss). The plugin
// heartbeats ~every 25s, so polling faster than that adds no information.
const POLL_INTERVAL_MS = 12000
// Safety net: only used to drop the connection if polls keep failing
// transiently (network error → null) while we were previously connected.
const DISCONNECT_TIMEOUT_MS = 40000
// When the watchdog fires while a poll is still awaiting a response, wait
// briefly for that authoritative result instead of racing it — but cap the
// wait so a hung request can't keep the connection alive indefinitely.
const DISCONNECT_DEFER_MS = 1000
// Defer long enough to outlast one full poll (which always settles within the
// fetch timeout), so an in-flight poll's result decides instead of racing the
// watchdog — otherwise a poll succeeding mid-defer emits a spurious toast pair.
// Derived from the fetch timeout so the two stay consistent.
const MAX_DISCONNECT_DEFERRALS =
  Math.ceil(CONNECTION_STATUS_TIMEOUT_MS / DISCONNECT_DEFER_MS) + 2

export interface LinkageContextType {
  connected: boolean
  connecting: boolean
  hasCheckedStatus: boolean
  hasConnectedOnce: boolean
  isWide: boolean
  code: string
  label: string
  hideCurrentChaff: () => Promise<void>
}

export const LinkageContext = createContext<LinkageContextType>({
  connected: false,
  connecting: true,
  hasCheckedStatus: false,
  hasConnectedOnce: false,
  isWide: false,
  code: '',
  label: 'Roblox Studio',
  hideCurrentChaff: async () => {},
})

export const LinkageProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslator()
  const { projectId, userId } = useProject()
  const code = useMemo(() => (userId ? btoa(userId) : ''), [userId])
  const isWide = !useBreakpoint(Breakpoint.phase2)
  const label = `Roblox Studio`

  const [connecting, setConnecting] = useState(true)
  const [hasConnectedOnce, setHasConnectedOnce] = useState(false)
  const [connected, setConnected] = useState(false)
  // 現在のプロジェクトで初回ポーリングの結果を採用するまで `connected: false` を
  // 「未接続」と断定しない（接続済みの生徒に「せつぞくのやりかた」を一瞬出さない
  // ため）。プロジェクトが変わるたびに（同じプロジェクトへ戻った場合も）未判定に
  // 戻し、古いポーリング結果で判定済みにしないよう ID で持つ。結果が null
  // （一時的な通信エラー）でも判定済みにする。通信が不安定な環境で案内が永遠に
  // 出なくなるのを避けるため。
  const [checkedProjectId, setCheckedProjectId] = useState<string | null>(null)
  const [statusProjectId, setStatusProjectId] = useState(projectId)
  if (statusProjectId !== projectId) {
    setStatusProjectId(projectId)
    setCheckedProjectId(null)
    setConnected(false)
    setConnecting(true)
  }
  const hasCheckedStatus = checkedProjectId === projectId
  const wasConnected = useRef(false)
  const disconnectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Holds the latest poll trigger so the visibility handler can run an
  // immediate, authoritative poll when the tab is re-focused.
  const pollRef = useRef<(() => void) | null>(null)
  // Mirror of `connected` so the visibility handler reads the latest value
  // without re-subscribing the listener on every state change.
  const connectedRef = useRef(false)
  // Identifies the poll request currently awaiting a response (null when idle)
  // so the disconnect watchdog can defer instead of racing an in-flight poll.
  const pollInFlightGeneration = useRef<number | null>(null)
  const nextPollGeneration = useRef(0)

  // Correlation id so connection diagnostics for one editor session group together.
  useEffect(() => {
    setTag('linkage_session', crypto.randomUUID())
  }, [])

  useEffect(() => {
    if (connected && !hasConnectedOnce) {
      setHasConnectedOnce(true)
    }
  }, [connected, hasConnectedOnce])

  useEffect(() => {
    connectedRef.current = connected
  }, [connected])

  const clearDisconnectTimeout = useCallback(() => {
    if (disconnectTimeout.current) {
      clearTimeout(disconnectTimeout.current)
      disconnectTimeout.current = null
    }
  }, [])

  const armDisconnectTimeout = useCallback(() => {
    clearDisconnectTimeout()
    // Cap how long we defer for an in-flight poll so a hung fetch (one that
    // never settles) can't keep us "connected" forever — the watchdog must
    // still be able to drop the connection.
    let deferralsLeft = MAX_DISCONNECT_DEFERRALS
    const fire = () => {
      disconnectTimeout.current = null
      // A backgrounded tab throttles timers; never drop while hidden. The
      // visibility handler re-polls on return to get ground truth.
      if (document.hidden) return
      // If a poll is mid-flight, defer briefly and let its authoritative result
      // decide — otherwise a slow poll returning `true` right after the watchdog
      // fires would emit both a "disconnected" and a "connected" toast.
      if (pollInFlightGeneration.current !== null && deferralsLeft > 0) {
        deferralsLeft -= 1
        disconnectTimeout.current = setTimeout(fire, DISCONNECT_DEFER_MS)
        return
      }
      setConnected(false)
      setConnecting(true)
    }
    disconnectTimeout.current = setTimeout(fire, DISCONNECT_TIMEOUT_MS)
  }, [clearDisconnectTimeout])

  // Single polling loop shared by every linkage consumer.
  useEffect(() => {
    // 接続状態はプロジェクト単位。プロジェクト切替で connected を戻したことを
    // 「切断」とみなしてトーストを出さないよう、直前の接続記録もここで戻す。
    wasConnected.current = false
    if (!projectId || !userId) {
      return
    }

    let cancelled = false
    // Generation-local guards (not shared refs): a stale poll from a previous
    // effect generation must not corrupt the current one's busy state.
    let inFlight = false
    let repollAfterInFlight = false

    const poll = async (queueIfBusy = false) => {
      // A backgrounded tab throttles timers, so a delayed/missed poll must not
      // flip the connection state. The visibility handler re-polls on return.
      if (document.hidden) return
      if (inFlight) {
        // Don't drop a requested authoritative poll; run it once the current
        // request settles.
        if (queueIfBusy) repollAfterInFlight = true
        return
      }
      inFlight = true
      // Expose in-flight state to the watchdog (defined outside this effect) so
      // it can defer rather than race a slow poll.
      const generation = ++nextPollGeneration.current
      pollInFlightGeneration.current = generation
      try {
        const status = await getConnectionStatus(projectId)
        // Re-check hidden: the tab may have been backgrounded mid-request, in
        // which case we must not re-arm the watchdog or flip state.
        if (cancelled || document.hidden) return

        setCheckedProjectId(projectId)
        if (status === true) {
          // Healthy poll. Re-arm the 40s watchdog: if no further positive
          // arrives in time (e.g. polls start erroring with null), it drops us.
          setConnected(true)
          setConnecting(false)
          armDisconnectTimeout()
        } else if (status === false) {
          // Server says the heartbeat is stale → genuinely disconnected.
          clearDisconnectTimeout()
          setConnected(false)
          setConnecting(true)
        }
        // status === null: transient network/HTTP error; leave state as-is and
        // let the watchdog drop the connection if errors persist past 40s.
      } finally {
        if (pollInFlightGeneration.current === generation) {
          pollInFlightGeneration.current = null
        }
        inFlight = false
        if (document.hidden) {
          // Don't carry a queued re-poll across a hide; the visibility handler
          // triggers a fresh one on return.
          repollAfterInFlight = false
        } else if (!cancelled && repollAfterInFlight) {
          repollAfterInFlight = false
          void poll()
        }
      }
    }

    const triggerPoll = () => void poll(true)
    pollRef.current = triggerPoll
    void poll()
    const interval = setInterval(() => void poll(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      if (pollRef.current === triggerPoll) {
        pollRef.current = null
      }
      clearInterval(interval)
      clearDisconnectTimeout()
    }
  }, [projectId, userId, armDisconnectTimeout, clearDisconnectTimeout])

  // Pause the disconnect watchdog while the tab is hidden (background timers
  // are throttled, so it would falsely fire and emit a spurious "disconnected"
  // toast), and re-poll on return so the real connection state drives the UI.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        clearDisconnectTimeout()
        return
      }
      // Restore the safety net before polling: if we were connected, a run of
      // post-return network errors (null) must still be able to drop us.
      if (connectedRef.current) {
        armDisconnectTimeout()
      }
      pollRef.current?.()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [armDisconnectTimeout, clearDisconnectTimeout])

  // Toast on connection transitions.
  useEffect(() => {
    if (connected && !wasConnected.current) {
      toast.success(t('connection_established'), {
        toastId: 'connection_established',
      })
    } else if (!connected && wasConnected.current) {
      toast.error(t('connection_lost_error'), {
        toastId: 'connection_lost_error',
      })
    }
    wasConnected.current = connected
  }, [connected, t])

  const hideCurrentChaff = useCallback(async () => {
    const workspaceDiv = document.querySelector(
      '.blockly-div:not(.blockly-div-hidden)',
    ) as HTMLElement
    if (!workspaceDiv) return
    const workspace = Blockly.getMainWorkspace() as WorkspaceSvg
    const activeElement = document.activeElement
    if (activeElement && activeElement !== document.body) {
      ;(activeElement as HTMLElement).blur()
    }
    workspace?.hideChaff()
    if (workspace) {
      missionManager.serializeCurrentWorkspace(workspace)
    }
  }, [])

  const value = useMemo(
    () => ({
      connected,
      connecting,
      hasCheckedStatus,
      hasConnectedOnce,
      isWide,
      code,
      label,
      hideCurrentChaff,
    }),
    [
      connected,
      connecting,
      hasCheckedStatus,
      hasConnectedOnce,
      isWide,
      code,
      label,
      hideCurrentChaff,
    ],
  )

  return (
    <LinkageContext.Provider value={value}>{children}</LinkageContext.Provider>
  )
}
